import {
    ContractTermsEnum,
    CreateWeightsProfilesInput,
    EntityPlanAttributes,
    GetPaperPlanMetadataOutput,
    PaperPlan,
    PaperPlanAttributes,
    PaperPlanIds,
    PaperPlanMetadata,
    PaperPlanWeightGroups,
    PaperPlanWeights,
    PerformanceDiscountNamesEnum,
    PerformanceDiscountsSpatialPlaysEnum,
    PlanDiscountRate,
    PlanWeightsEnum,
    WeightsProfiles
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { find } from 'lodash';
import { getPlanWeightName } from 'src/components/AddPaperPlan/AddWeights/addWeightsUtils';
import { PLAN_WEIGHTS_TABS } from 'src/components/PlanWeights/planWeightsUtils';
import {
    CorePlan,
    PaperPlanCorePlan,
    PaperPlanGroup,
    PaperPlanPlanType,
    PaperPlanRateGroup,
    PlanWeightsTab
} from 'src/constants';
import { GLOBAL_TERRITORY } from 'src/constants/context';
import { CORE_PLAN_IDS, GLOBAL_PLAN_METADATA_TERRITORY_CODE } from 'src/constants/paperPlans';
import { IPlanValues } from 'src/types/IPaperPlan';
import { IFirstPartyCorePlan, IMultiplierType } from 'src/types/IRateCard';
import { IWeightsProfile } from 'src/types/IWeightsProfile';
import {
    convertDateStringToDateObj,
    convertTimestampToYYYYMMDDInUTCTimezone,
    dateYYYYMMReg
} from 'src/util/dateTimeUtil';

export const getPlanGroup = (planValues: IPlanValues) => {
    if (planValues.customerPays && planValues.royaltyBearing && !planValues.carrierPlan && planValues.renewalCadence) {
        return PaperPlanGroup.Standard;
    } else if (
        planValues.promoDurationInDays &&
        [14, 30].includes(parseInt(planValues.promoDurationInDays)) &&
        !planValues.customerPays &&
        !planValues.royaltyBearing &&
        !planValues.employeePlan &&
        !planValues.carrierPlan
    ) {
        return PaperPlanGroup.FreeTrial;
    } else if (
        planValues.promoDurationInDays &&
        ![14, 30].includes(parseInt(planValues.promoDurationInDays)) &&
        !planValues.customerPays &&
        planValues.royaltyBearing &&
        !planValues.carrierPlan
    ) {
        return PaperPlanGroup.ExtendedTrial;
    } else if (
        planValues.promoDurationInDays &&
        ![14, 30].includes(parseInt(planValues.promoDurationInDays)) &&
        planValues.customerPays &&
        planValues.royaltyBearing &&
        !planValues.carrierPlan
    ) {
        return PaperPlanGroup.PriceDrop;
    } else if (planValues.employeePlan) {
        return PaperPlanGroup.Employee;
    } else if (
        planValues.promoDurationInDays &&
        [14, 30].includes(parseInt(planValues.promoDurationInDays)) &&
        !planValues.customerPays &&
        !planValues.royaltyBearing &&
        !planValues.employeePlan &&
        planValues.carrierPlan
    ) {
        return PaperPlanGroup.CarrierFreeTrial;
    } else if (
        planValues.promoDurationInDays &&
        ![14, 30].includes(parseInt(planValues.promoDurationInDays)) &&
        planValues.royaltyBearing &&
        planValues.carrierPlan
    ) {
        return PaperPlanGroup.CarrierIntro;
    } else if (
        planValues.customerPays &&
        planValues.royaltyBearing &&
        planValues.carrierPlan &&
        planValues.renewalCadence
    ) {
        return PaperPlanGroup.CarrierStandard;
    } else {
        return '';
    }
};

export const getRateGroup = (planValues: IPlanValues) => {
    if (CORE_PLAN_IDS.includes(planValues.paperPlanName as CorePlan)) {
        return PaperPlanRateGroup.Core;
    } else if (
        planValues.customerPays &&
        planValues.royaltyBearing &&
        !planValues.carrierPlan &&
        planValues.renewalCadence &&
        planValues.renewalCadence !== 'Monthly'
    ) {
        return PaperPlanRateGroup.NonMonthly;
    } else if (planValues.royaltyBearing && !planValues.carrierPlan && planValues.renewalCadence !== 'Monthly') {
        return PaperPlanRateGroup.FirstPartyPromo;
    } else if (
        planValues.promoDurationInDays &&
        ![14, 30].includes(parseInt(planValues.promoDurationInDays)) &&
        planValues.royaltyBearing &&
        planValues.carrierPlan
    ) {
        return PaperPlanRateGroup.CarrierIntro;
    } else if (
        planValues.customerPays &&
        planValues.royaltyBearing &&
        planValues.carrierPlan &&
        planValues.renewalCadence
    ) {
        return PaperPlanRateGroup.CarrierStandard;
    } else if (!planValues.royaltyBearing) {
        return PaperPlanRateGroup.FreeTrial;
    } else {
        return '';
    }
};

/**
 * Applies business rules & returns the core plan name based on the given Paper Plan values
 * @param planValues IPlanValues interface containing all PaperPlan data including metadata attributes
 * @returns string representing the core plan name
 */
export const getCorePlan = (planValues: IPlanValues): string => {
    switch (planValues.planType) {
        case PaperPlanPlanType.Individual:
            if (planValues.isHD) {
                return PaperPlanCorePlan.HDIndM;
            } else {
                return planValues.isBundle ? PaperPlanCorePlan.MontanaIndM : PaperPlanCorePlan.IndM;
            }
        case PaperPlanPlanType.Family:
            if (planValues.isHD) {
                return PaperPlanCorePlan.HDFamM;
            } else {
                return planValues.isBundle ? PaperPlanCorePlan.MontanaFamM : PaperPlanCorePlan.FamM;
            }
        case PaperPlanPlanType.Student:
            return PaperPlanCorePlan.StudentM;
        case PaperPlanPlanType.SingleDevice:
            return PaperPlanCorePlan.ASDM;
        default:
            return '';
    }
};

export const getPlanAttributes = (attributes: EntityPlanAttributes) => {
    return {
        employeePlan: convertToBoolean(getAttributeValue(attributes, 'employeePlan')),
        carrierPlan: convertToBoolean(getAttributeValue(attributes, 'carrierPlan')),
        royaltyBearing: convertToBoolean(getAttributeValue(attributes, 'royaltyBearing')),
        customerPays: convertToBoolean(getAttributeValue(attributes, 'customerPays')),
        isRenewalCadenceChecked: convertToBoolean(getAttributeValue(attributes, 'isRenewalCadenceChecked')),
        renewalCadence: getAttributeValue(attributes, 'renewalCadence'),
        promoDurationInDays: getAttributeValue(attributes, 'promoDurationInDays'),
        wholesalePrice: getAttributeValue(attributes, 'wholesalePrice'),
        wholesalePriceCurrency: getAttributeValue(attributes, 'wholesalePriceCurrency'),
        endUserPrice: getAttributeValue(attributes, 'endUserPrice'),
        endUserPriceCurrency: getAttributeValue(attributes, 'endUserPriceCurrency'),
        planType: getAttributeValue(attributes, 'planType'),
        isPromotion: convertToBoolean(getAttributeValue(attributes, 'isPromotion')),
        isHD: convertToBoolean(getAttributeValue(attributes, 'isHD')),
        isBundle: convertToBoolean(getAttributeValue(attributes, 'isBundle')),
        promoMultiplier: getAttributeValue(attributes, 'promoMultiplier'),
        reportingName: getAttributeValue(attributes, 'reportingName'),
        subscriptionProduct: getAttributeValue(attributes, 'subscriptionProduct'),
        affiliateName: getAttributeValue(attributes, 'affiliateName')
    };
};

export const convertToBoolean = (value: string | undefined) => {
    return value === 'true';
};

export const getAttributeValue = (attributes: EntityPlanAttributes, attribute: string) => {
    const entityPlanAttribute = attributes?.filter((element) => {
        return element.attribute === attribute;
    });
    return entityPlanAttribute?.[0]?.value;
};

export const getPromoDurationDisplayNumber = (durationStr: string) => {
    return isNaN(parseInt(durationStr)) ? '' : parseInt(durationStr);
};

export const getPlanDate = (dateStr: string, isEndDate = false) => {
    if (dateStr && !dateYYYYMMReg.test(dateStr)) {
        const yearStr = dateStr.substring(0, 4);
        const monthStr = dateStr.substring(4, 6);
        const dayStr = dateStr.substring(6, 8);

        if (isEndDate) {
            const lastDayOfMonthDate = new Date(Number(yearStr), Number(monthStr), 0);
            const [month, day, year] = lastDayOfMonthDate.toLocaleDateString().split('/');
            return `${year}-${Number(month) < 10 ? '0' + month : month}-${day}`;
        }

        return `${yearStr}-${monthStr}${dayStr ? '-' + dayStr : ''}`;
    }
    return dateStr;
};

export const getPlanYYYYMMDDUTCDate = (dateStr: string, isEndDate = false) => {
    return convertTimestampToYYYYMMDDInUTCTimezone(convertDateStringToDateObj(getPlanDate(dateStr, isEndDate)));
};

export const mapPaperPlanMetadataToPlanValues = (paperPlanMetadata: PaperPlanMetadata) => {
    return {
        paperPlanId: paperPlanMetadata.paperPlan?.paperPlanId || '',
        paperPlanName: paperPlanMetadata.paperPlan?.paperPlanName || '',
        description: paperPlanMetadata.paperPlan?.description || '',
        displayName: paperPlanMetadata.paperPlan?.displayName || '',
        endDate: paperPlanMetadata.paperPlan?.endDate || '',
        startDate: paperPlanMetadata.paperPlan?.startDate || '',
        serviceTier: paperPlanMetadata.paperPlan?.serviceTier || '',
        territory: paperPlanMetadata.paperPlan?.territory || '',
        changedBy: paperPlanMetadata.paperPlan?.changedBy || '',
        changedDate: paperPlanMetadata.paperPlan?.changeDate || '',
        creationDate: paperPlanMetadata.paperPlan?.creationDate || '',
        createdBy: paperPlanMetadata.paperPlan?.createdBy || '',
        isDraft: paperPlanMetadata.paperPlan?.isDraft || false,
        isDeleted: paperPlanMetadata.paperPlan?.isDeleted || false,
        attributeSetId: paperPlanMetadata.attributeSet?.[0].planAttributeSetId || '',
        ...getPlanAttributes(paperPlanMetadata.attributeSet?.[0].attributes || [])
    };
};

export const getTerritory = (territoryCode: string) => {
    return territoryCode === GLOBAL_PLAN_METADATA_TERRITORY_CODE ? GLOBAL_TERRITORY.NAME : territoryCode;
};

export const getPaperPlanAttributesInput = (planValues: IPlanValues) => {
    return {
        carrierPlan: planValues.carrierPlan.toString(),
        customerPays: planValues.customerPays.toString(),
        employeePlan: planValues.employeePlan.toString(),
        isRenewalCadenceChecked: planValues.isRenewalCadenceChecked.toString(),
        royaltyBearing: planValues.royaltyBearing.toString(),
        planType: planValues.planType,
        planGroup: getPlanGroup(planValues),
        reportingName: planValues.reportingName,
        affiliateName: planValues.affiliateName,
        isPromotion: getPaperPlanIsPromoAttributeInput(planValues),
        isBundle: planValues.isBundle.toString(),
        ...(planValues.endUserPrice && { endUserPrice: planValues.endUserPrice }),
        ...(planValues.endUserPriceCurrency && { endUserPriceCurrency: planValues.endUserPriceCurrency }),
        ...(planValues.promoDurationInDays && { promoDurationInDays: planValues.promoDurationInDays }),
        ...(planValues.renewalCadence && { renewalCadence: planValues.renewalCadence }),
        ...(planValues.wholesalePrice && { wholesalePrice: planValues.wholesalePrice }),
        ...(planValues.wholesalePriceCurrency && { wholesalePriceCurrency: planValues.wholesalePriceCurrency }),
        ...(planValues.promoMultiplier &&
            planValues.isBundle &&
            getPaperPlanIsPromoAttributeInput(planValues) === 'true' && {
                promoMultiplier: planValues.promoMultiplier
            }),
        ...(planValues.subscriptionProduct && { subscriptionProduct: planValues.subscriptionProduct })
    } as PaperPlanAttributes;
};

export const getPaperPlanIsPromoAttributeInput = (planValues: IPlanValues) => {
    if (planValues.customerPays && planValues.royaltyBearing && planValues.renewalCadence) {
        return 'false';
    } else {
        return 'true';
    }
};

export const isPromoPlan = (planValues: IPlanValues): boolean => {
    return !(planValues.customerPays && planValues.royaltyBearing && planValues.renewalCadence);
};

/**
 * Returns a list of all core plans based on the given GetPaperPlanMetadataOutput
 * @param planMetadataResponse GetPaperPlanMetadataOutput interface containing all PaperPlan data including metadata attributes
 * @returns IFirstPartyCorePlan[] representing the core plan list
 */
export const getCorePlanList = (planMetadataResponse: GetPaperPlanMetadataOutput): IFirstPartyCorePlan[] => {
    const corePlanList: IFirstPartyCorePlan[] = [];

    planMetadataResponse.planMetadata?.forEach((plan: PaperPlanMetadata) => {
        const paperPlan: PaperPlan = plan.paperPlan as PaperPlan;

        if (isCorePlan(paperPlan)) {
            corePlanList.push(buildFirstPartyCorePlanFrom(paperPlan));
        }
    });

    return corePlanList;
};

/**
 * Returns whether the given paperPlan is a core plan or not
 * @param paperPlan PaperPlan object
 * @returns boolean indicating whether the given paperPlan is a core plan or not
 */
export const isCorePlan = (paperPlan: PaperPlan): boolean => {
    return (
        !paperPlan?.isDraft &&
        !paperPlan?.isDeleted &&
        !!paperPlan?.paperPlanName &&
        CORE_PLAN_IDS.includes(paperPlan.paperPlanName as CorePlan)
    );
};

/**
 * Returns the IFirstPartyCorePlan object based on the given PaperPlan
 * @param paperPlan PaperPlan object
 * @returns IFirstPartyCorePlan object containing the corePlanId, corePlanName, and corePlanDisplayName
 */
export const buildFirstPartyCorePlanFrom = (paperPlan: PaperPlan): IFirstPartyCorePlan => {
    return {
        corePlanId: paperPlan.paperPlanId?.toString() || '',
        corePlanName: paperPlan.paperPlanName || '',
        corePlanDisplayName: paperPlan.displayName || ''
    };
};

/**
 * Returns the PlanDiscountRate object based on the given derivedMultiplierName, multiplierTypeList, corePlanDisplayName, and corePlanList
 * @param derivedMultiplierName string representing the derived multiplier type name for the plan discount rate
 * @param multiplierTypeList IMultiplierType[] representing the list of multiplier types, and it's corresponding Ids
 * @param corePlanDisplayName string representing the core plan display name for a given derived plan
 * @param corePlanList IFirstPartyCorePlan[] representing the list of all core plans, and it's corresponding Ids
 * @returns PlanDiscountRate object containing the rateMultiplierTypeId and corePlanId
 */
export const getPlanDiscountRate = (
    derivedMultiplierName: string,
    multiplierTypeList: IMultiplierType[],
    corePlanDisplayName: string,
    corePlanList: IFirstPartyCorePlan[]
): PlanDiscountRate | null => {
    if (corePlanDisplayName === '') {
        return null;
    }
    return PlanDiscountRate.fromJson({
        rateMultiplierTypeId: (find(multiplierTypeList, { displayName: derivedMultiplierName }) as IMultiplierType)
            ?.rateMultiplierTypeId,
        corePlanId: Number(
            (find(corePlanList, { corePlanDisplayName: corePlanDisplayName }) as IFirstPartyCorePlan)?.corePlanId
        )
    });
};

export function getWeightsProfilesInput(
    createdBy: string,
    planWeights: Map<PlanWeightsTab, IWeightsProfile>,
    planIds: PaperPlanIds
): CreateWeightsProfilesInput {
    // TODO services not returned in service metadata, so hard code IDs here
    const [AMU_SERVICE_ID, PRIME_SERVICE_ID, AMF_SERVICE_ID] = [1, 2, 3];
    const weightsProfiles: WeightsProfiles = [];

    planWeights.forEach((weights, entity) => {
        const planWeightsGroups: PaperPlanWeightGroups = [];

        planIds.forEach((planId) => {
            const paperPlanWeights: PaperPlanWeights = [];

            Object.entries(weights).forEach(([weightType, weightValue]) => {
                if (weightValue !== '' && weightValue !== undefined) {
                    switch (weightType) {
                        case 'amuAlexaPsmNumerator':
                            paperPlanWeights.push({
                                name: getPlanWeightName(weightType, entity),
                                serviceId: AMU_SERVICE_ID,
                                type: ContractTermsEnum.NUMERATOR,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuAlexaPsmDenominator':
                            paperPlanWeights.push({
                                name: getPlanWeightName(weightType, entity),
                                serviceId: AMU_SERVICE_ID,
                                type: ContractTermsEnum.DENOMINATOR,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuBlendedPsm':
                            paperPlanWeights.push({
                                name: PlanWeightsEnum.AMU_BLENDED_PSM_CALC,
                                serviceId: AMU_SERVICE_ID,
                                type: ContractTermsEnum.WEIGHTS,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuDiscount1PreSpatial':
                            paperPlanWeights.push({
                                name: PerformanceDiscountNamesEnum.PERFORMANCE_DISCOUNT_1_GLOBAL_AMU_SUBS,
                                serviceId: AMU_SERVICE_ID,
                                type: PerformanceDiscountsSpatialPlaysEnum.TIER_1_PRE_SPATIAL_PLAYS,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuDiscount2PreSpatial':
                            paperPlanWeights.push({
                                name: PerformanceDiscountNamesEnum.PERFORMANCE_DISCOUNT_2_AMU_SUBS_OVER_ALL,
                                serviceId: AMU_SERVICE_ID,
                                type: PerformanceDiscountsSpatialPlaysEnum.TIER_2_PRE_SPATIAL_PLAYS,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuDiscount1PostSpatial':
                            paperPlanWeights.push({
                                name: PerformanceDiscountNamesEnum.PERFORMANCE_DISCOUNT_1_GLOBAL_AMU_SUBS,
                                serviceId: AMU_SERVICE_ID,
                                type: PerformanceDiscountsSpatialPlaysEnum.TIER_1_POST_SPATIAL_PLAYS,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuDiscount2PostSpatial':
                            paperPlanWeights.push({
                                name: PerformanceDiscountNamesEnum.PERFORMANCE_DISCOUNT_2_AMU_SUBS_OVER_ALL,
                                serviceId: AMU_SERVICE_ID,
                                type: PerformanceDiscountsSpatialPlaysEnum.TIER_2_POST_SPATIAL_PLAYS,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuDiscount3':
                            paperPlanWeights.push({
                                name: PerformanceDiscountNamesEnum.PERFORMANCE_DISCOUNT_3_CHURN,
                                serviceId: AMU_SERVICE_ID,
                                type: 'plan weights for performance discount 3',
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuDiscount4Ft':
                            paperPlanWeights.push({
                                name: PerformanceDiscountNamesEnum.PERFORMANCE_DISCOUNT_4_FT_UPGRADES_TO_PAID,
                                serviceId: AMU_SERVICE_ID,
                                type: 'FT plan weights for performance discount 4',
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amuDiscount4Amu':
                            paperPlanWeights.push({
                                name: PerformanceDiscountNamesEnum.PERFORMANCE_DISCOUNT_4_FT_UPGRADES_TO_PAID,
                                serviceId: AMU_SERVICE_ID,
                                type: 'AMU plan weights for performance discount 4',
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amfConversionNumerator':
                            paperPlanWeights.push({
                                name: getPlanWeightName(weightType, entity),
                                serviceId: AMF_SERVICE_ID,
                                type: ContractTermsEnum.NUMERATOR,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'amfConversionDenominator':
                            paperPlanWeights.push({
                                name: getPlanWeightName(weightType, entity),
                                serviceId: AMF_SERVICE_ID,
                                type: ContractTermsEnum.DENOMINATOR,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'primePrimaryNumerator':
                            paperPlanWeights.push({
                                name: getPlanWeightName(weightType, entity),
                                serviceId: PRIME_SERVICE_ID,
                                type: ContractTermsEnum.NUMERATOR,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'primePrimaryDenominator':
                            paperPlanWeights.push({
                                name: getPlanWeightName(weightType, entity),
                                serviceId: PRIME_SERVICE_ID,
                                type: ContractTermsEnum.DENOMINATOR,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'primeSecondaryNumerator':
                            paperPlanWeights.push({
                                name: getPlanWeightName(weightType, entity),
                                serviceId: PRIME_SERVICE_ID,
                                type: ContractTermsEnum.NUMERATOR,
                                value: parseFloat(weightValue)
                            });
                            break;

                        case 'primeSecondaryDenominator':
                            paperPlanWeights.push({
                                name: getPlanWeightName(weightType, entity),
                                serviceId: PRIME_SERVICE_ID,
                                type: ContractTermsEnum.DENOMINATOR,
                                value: parseFloat(weightValue)
                            });
                            break;
                        default:
                            throw new Error(`Unrecognized weight type ${weightType}`);
                    }
                }
            });

            planWeightsGroups.push({
                paperPlanId: planId,
                paperPlanWeights: paperPlanWeights
            });
        });

        weightsProfiles.push({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            entityId: PLAN_WEIGHTS_TABS.find((tab) => tab.name === entity)!.entityId,
            planWeightsGroups: planWeightsGroups
        });
    });

    return {
        createdBy: createdBy,
        weightsProfiles: weightsProfiles
    };
}
